import { getFutureMatches } from '@datasert/cronjs-matcher';
import { type LocaleDefinition } from '@solinkcorp/locale';
import { type SelectOption } from '@solinkcorp/solink-components';
import { ScheduledActivityType } from 'clients/CallhomeApiTypes';
import { type AIInspectionDeviceType } from 'components/ScheduledActivityForm/ScheduledActivityForm';
import {
  type NewActivityType,
  type ScheduledExceptionsBaseAnalytics,
  type ScheduledExceptionsPreviousEventAnalytics,
} from 'components/ScheduledActivityForm/useScheduledEventForm';
import moment from 'moment';
import { DEFAULT_EVENT_FITLERS } from 'stores/EventFilterStore';
import { getLocalizedStr } from 'utils/LocaleUtil';
import {
  DOES_NOT_REPEAT,
  EVENT_SUBTYPE_SCHEDULED,
  HIDE_FROM_DISPLAY_TAG,
  OCCUR_NEVER,
  OCCUR_ONCE,
  REOCCUR_INDEFINITE,
  REOCCUR_NEVER_END,
  REPEAT_ANNUALLY,
  REPEAT_DAILY,
  REPEAT_EVERY_15MIN,
  REPEAT_EVERY_30MIN,
  REPEAT_EVERY_4H,
  REPEAT_EVERY_6H,
  REPEAT_HOURLY,
  REPEAT_MONTHLY,
  REPEAT_WEEKLY,
  type TypeOfAlarmException,
} from 'vd-constants';
import CommonUtil from './CommonUtil';
import { generateQueryParameters, getEventsFromPlatform } from './EventsFetchUtil';
import TimeFormatter from './TimeFormatter';

/** Run on for all values for the relevent field  */
export const CRON_ALL_VALUES = '*';

const ALL_DAYS_OF_WEEK = new Set([0, 1, 2, 3, 4, 5, 6]);
function isAllDaysOfWeek(day: string): boolean {
  if (day === CRON_ALL_VALUES) return true;
  const days = new Set(day.split(',').map(Number));
  return days.size === 7 && [...days].every((d) => ALL_DAYS_OF_WEEK.has(d));
}

export const INIT_CRON: [string, string, string, string, string] = [
  '*', // minute
  '*', // hour
  '*', // date - day of month
  '*', // month
  '*', // day of week
];

const CRON_FIELD_INDEXES = {
  MINUTE: 0,
  HOUR: 1,
  DATE: 2,
  MONTH: 3,
  DAY_OF_WEEK: 4,
} as const;

const monthDateFormat = 'MMM D';
const ONE_HOUR_MILLISEC = 3600000;

const padl0 = (n: string) => (n.length < 2 ? `0${n}` : `${n}`);

/**
 * Get the daily repeat dropdown option's display text, based on the start time
 * @param time
 * @param locale
 * @returns {string}
 */
export const getDailyOptionString = (time: moment.Moment | undefined, locale: LocaleDefinition) => {
  return getLocalizedStr({
    string: locale.general.dailyAtTime,
    replacements: { time: time?.format('HH:mm') },
  });
};

/**
 * Get the daily repeat dropdown option's display text, based on the start time
 * @param time
 * @param locale
 * @returns {string}
 */
export const getEvery6hOptionString = (
  time: moment.Moment | undefined,
  locale: LocaleDefinition,
) => {
  return getLocalizedStr({
    string: locale.general.every6hStartingAt,
    replacements: { time: time?.format('HH:mm') },
  });
};

/**
 * Get the daily repeat dropdown option's display text, based on the start time
 * @param time
 * @param locale
 * @returns {string}
 */
export const getEvery4hOptionString = (
  time: moment.Moment | undefined,
  locale: LocaleDefinition,
) => {
  return getLocalizedStr({
    string: locale.general.every4hStartingAt,
    replacements: { time: time?.format('HH:mm') },
  });
};

export const getEveryHourOptionString = (
  time: moment.Moment | undefined,
  locale: LocaleDefinition,
) => {
  return getLocalizedStr({
    string: locale.general.everyHourStartingAt,
    replacements: { minutes: time?.minutes().toString() },
  });
};

export const getEvery15minOptionString = (
  time: moment.Moment | undefined,
  locale: LocaleDefinition,
) => {
  if (!time) return '';

  const now = moment();
  const roundedTime = time
    .clone()
    .startOf('hour')
    .add(Math.round(time.minutes() / 15) * 15, 'minutes');

  // If the rounded time is within 5 minutes of now, round up to the next 15-min interval
  const timeDiff = roundedTime.diff(now, 'minutes');
  const finalTime = timeDiff <= 5 ? roundedTime.add(15, 'minutes') : roundedTime;

  return getLocalizedStr({
    string: locale.general.every15minStartingAt,
    replacements: { time: finalTime.format('HH:mm') },
  });
};

export const getEvery30minOptionString = (
  time: moment.Moment | undefined,
  locale: LocaleDefinition,
) => {
  if (!time) return '';

  const now = moment();
  const roundedTime = time
    .clone()
    .startOf('hour')
    .add(Math.round(time.minutes() / 30) * 30, 'minutes');

  // If the rounded time is within 5 minutes of now, round up to the next 30-min interval
  const timeDiff = roundedTime.diff(now, 'minutes');
  const finalTime = timeDiff <= 5 ? roundedTime.add(30, 'minutes') : roundedTime;

  return getLocalizedStr({
    string: locale.general.every30minStartingAt,
    replacements: { time: finalTime.format('HH:mm') },
  });
};

/**
 * Get the weekly repeat dropdown option's display text, based on the start time
 * @param time
 * @param locale
 * @returns {string}
 */
export const getWeeklyOptionString = (
  time: moment.Moment | undefined,
  locale: LocaleDefinition,
) => {
  return getLocalizedStr({
    string: locale.general.weeklyOnDayAtTime,
    replacements: {
      time: time?.format('HH:mm'),
      day: time?.format('dddd'),
    },
  });
};

/**
 * Get the monthly repeat dropdown option's display text, based on the start time
 * @param time
 * @param locale
 * @returns {string}
 */
export const getMonthlyOptionString = (time: Date | number, locale: LocaleDefinition) => {
  const date = new Date(time).getDate().toString();
  return getLocalizedStr({
    string: locale.general.monthlyOnDate,
    replacements: { date },
  });
};

/**
 * Get the annually repeat dropdown option's display text, based on the start time
 * @param time
 * @param locale
 * @returns {string}
 */
export const getAnnualOptionString = (time: Date | number, locale: LocaleDefinition) => {
  const date = moment(time).format(monthDateFormat);
  return getLocalizedStr({
    string: locale.general.annuallyOnDate,
    replacements: { date },
  });
};

/**
 * Get the repeat dropdown's options, based on the start time
 * @param time
 * @param locale
 * @returns {array}
 */
export const getRepeatOptions = (
  time: Date | number,
  locale: LocaleDefinition,
  timezoneId: string,
  includeHourlyRepeat = false,
  includeMinuteRepeat = false,
): SelectOption[] => {
  const deviceTime = moment(time).tz(timezoneId);
  return [
    {
      value: DOES_NOT_REPEAT,
      label: locale.general.doesNotRepeat,
    },
    ...(includeMinuteRepeat
      ? [
          {
            value: REPEAT_EVERY_15MIN,
            label: getEvery15minOptionString(deviceTime, locale),
          },
          {
            value: REPEAT_EVERY_30MIN,
            label: getEvery30minOptionString(deviceTime, locale),
          },
        ]
      : []),
    ...(includeHourlyRepeat
      ? [
          {
            value: REPEAT_HOURLY,
            label: getEveryHourOptionString(deviceTime, locale),
          },
        ]
      : []),
    {
      value: REPEAT_EVERY_4H,
      label: getEvery4hOptionString(deviceTime, locale),
    },
    {
      value: REPEAT_EVERY_6H,
      label: getEvery6hOptionString(deviceTime, locale),
    },
    {
      value: REPEAT_DAILY,
      label: getDailyOptionString(deviceTime, locale),
    },
    {
      value: REPEAT_WEEKLY,
      label: getWeeklyOptionString(deviceTime, locale),
    },
    {
      value: REPEAT_MONTHLY,
      label: getMonthlyOptionString(time, locale),
    },
    {
      value: REPEAT_ANNUALLY,
      label: getAnnualOptionString(time, locale),
    },
  ];
};
/**
 * Helper function to determine if hours are spaced evenly by X hours.
 * @param hourString Comma-separated hour values (e.g., "0,4,8,12")
 * @param interval Interval to check for (e.g., 4 for every 4 hours)
 * @returns {boolean}
 */
function isEveryXHours(hourString: string, interval: number): boolean {
  if (!hourString) return false;

  const hours = hourString.split(',').map(Number);

  if (hours.length < 2) {
    return false;
  }

  // Ensure the difference between consecutive hours is equal to the interval
  for (let i = 1; i < hours.length; i++) {
    const diff = (hours[i] - hours[i - 1] + 24) % 24;
    if (diff !== interval) {
      return false;
    }
  }

  // Also check the wrap-around case (from the last hour back to the first)
  const wrapAroundDiff = (hours[0] - hours[hours.length - 1] + 24) % 24;
  if (wrapAroundDiff !== interval) {
    return false;
  }

  return true;
}

/**
 * Get the repeat option's value, based on the CRON rule and reoccurringCount
 * @param rule
 * @param reoccurringCount
 * @returns {string}
 */
export const getRepeatOptionValue = (rule: string, reoccurringCount: number) => {
  let option = DOES_NOT_REPEAT;
  if (reoccurringCount > 1 || reoccurringCount === REOCCUR_INDEFINITE) {
    const { minute, date, day, hour, month } = parseCron(rule);

    // Check for minute intervals first
    if (minute === '*/15') {
      option = REPEAT_EVERY_15MIN;
    } else if (minute === '*/30') {
      option = REPEAT_EVERY_30MIN;
    }
    // Check if it's every hour
    else if (hour === CRON_ALL_VALUES) {
      option = REPEAT_HOURLY;

      // Check if it's every 4 hours (any sequence with 4-hour interval)
    } else if (isEveryXHours(hour, 4)) {
      option = REPEAT_EVERY_4H;

      // Check if it's every 6 hours (any sequence with 6-hour interval)
    } else if (isEveryXHours(hour, 6)) {
      option = REPEAT_EVERY_6H;

      // Check if it's daily, weekly, monthly, or annually
    } else if (date === CRON_ALL_VALUES && (day === CRON_ALL_VALUES || day.split(',').length > 1)) {
      option = REPEAT_DAILY;
    } else if (date === CRON_ALL_VALUES) {
      option = REPEAT_WEEKLY;
    } else if (month === CRON_ALL_VALUES) {
      option = REPEAT_MONTHLY;
    } else {
      option = REPEAT_ANNUALLY;
    }
  }
  return option;
};
export const getActivityTypeOptions = (locale: LocaleDefinition, filterReasons?: string[]) => {
  return [
    {
      value: ScheduledActivityType.ALARM_EXCEPTION,
      label: locale.general.alarmException,
    },
    {
      value: ScheduledActivityType.SCHEDULED_INSPECTION,
      label: locale.general.scheduledInspection,
    },
    {
      value: ScheduledActivityType.SCHEDULED_AI_CHAT,
      label: locale.general.scheduledAIChat,
    },
  ].filter(({ value }) => !filterReasons || filterReasons.includes(value));
};

/**
 * Parse the time info from the CRON rule (in Unix Cron format)
 * @param rule
 * @returns {object}
 */
export const parseCron = (rule: string) => {
  const ruleArray = rule.split(' ');
  const [minute, hour, date, month, day] = ruleArray;
  return { minute, hour, date, month, day };
};

/**
 * Get the recurrence info based on the start time and the repeat option.
 * Returns the Cron rule and reoccurring count
 * @param startTime
 * @param repeatOptionValue
 * @returns {object}
 */
export const getRecurrence = (
  startTime: Date | number,
  repeatOptionValue: string,
  timezoneId: string,
  daysOfWeek: number[],
) => {
  const cron = [...INIT_CRON];
  let reoccurringCount = 1; // only execute once - do not repeat

  const startTimeDate = moment(startTime).tz(timezoneId);

  const minute = startTimeDate.minutes().toString();
  const hour = startTimeDate.hours();
  const date = startTimeDate.date().toString();
  const month = (startTimeDate.month() + 1).toString();

  cron[CRON_FIELD_INDEXES.MINUTE] = minute;
  cron[CRON_FIELD_INDEXES.HOUR] = hour.toString();
  cron[CRON_FIELD_INDEXES.DATE] = date;
  cron[CRON_FIELD_INDEXES.MONTH] = month;

  switch (repeatOptionValue) {
    case REPEAT_HOURLY:
      cron[CRON_FIELD_INDEXES.HOUR] = '*';
      cron[CRON_FIELD_INDEXES.DATE] = '*';
      cron[CRON_FIELD_INDEXES.MONTH] = '*';
      reoccurringCount = REOCCUR_INDEFINITE;
      break;

    case REPEAT_EVERY_15MIN:
      cron[CRON_FIELD_INDEXES.MINUTE] = '*/15';
      cron[CRON_FIELD_INDEXES.HOUR] = '*';
      cron[CRON_FIELD_INDEXES.DATE] = '*';
      cron[CRON_FIELD_INDEXES.MONTH] = '*';
      reoccurringCount = REOCCUR_INDEFINITE;
      break;

    case REPEAT_EVERY_30MIN:
      cron[CRON_FIELD_INDEXES.MINUTE] = '*/30';
      cron[CRON_FIELD_INDEXES.HOUR] = '*';
      cron[CRON_FIELD_INDEXES.DATE] = '*';
      cron[CRON_FIELD_INDEXES.MONTH] = '*';
      reoccurringCount = REOCCUR_INDEFINITE;
      break;

    case REPEAT_EVERY_4H:
      cron[CRON_FIELD_INDEXES.HOUR] = `${hour},${(hour + 4) % 24},${(hour + 8) % 24},${
        (hour + 12) % 24
      },${(hour + 16) % 24},${(hour + 20) % 24}`;
      cron[CRON_FIELD_INDEXES.DATE] = '*';
      cron[CRON_FIELD_INDEXES.MONTH] = '*';
      reoccurringCount = REOCCUR_INDEFINITE;
      break;

    case REPEAT_EVERY_6H:
      cron[CRON_FIELD_INDEXES.HOUR] = `${hour},${(hour + 6) % 24},${(hour + 12) % 24},${
        (hour + 18) % 24
      }`;
      cron[CRON_FIELD_INDEXES.DATE] = '*';
      cron[CRON_FIELD_INDEXES.MONTH] = '*';
      reoccurringCount = REOCCUR_INDEFINITE;
      break;

    case REPEAT_DAILY:
      cron[CRON_FIELD_INDEXES.MONTH] = '*';
      cron[CRON_FIELD_INDEXES.DATE] = '*';
      reoccurringCount = REOCCUR_INDEFINITE;
      break;

    case REPEAT_WEEKLY:
      cron[CRON_FIELD_INDEXES.MONTH] = '*';
      cron[CRON_FIELD_INDEXES.DATE] = '*';
      cron[CRON_FIELD_INDEXES.DAY_OF_WEEK] = startTimeDate.day().toString();
      reoccurringCount = REOCCUR_INDEFINITE;
      break;

    case REPEAT_MONTHLY:
      cron[CRON_FIELD_INDEXES.MONTH] = '*';
      reoccurringCount = REOCCUR_INDEFINITE;
      break;

    case REPEAT_ANNUALLY:
      reoccurringCount = REOCCUR_INDEFINITE;
      break;
  }

  /** Update days of week field */
  if (repeatOptionValue === REPEAT_DAILY && daysOfWeek.length > 0) {
    cron[CRON_FIELD_INDEXES.DAY_OF_WEEK] = daysOfWeek.join(',');
  }

  return {
    cron: cron.join(' '),
    reoccurringCount,
  };
};

/**
 * Get the repeat option's display text, based on the Cron rule and reoccuring count
 * @returns {string}
 */
export const getRepeatOptionString = (
  {
    rule,
    reoccurringCount,
    durationSecs,
    startsAt,
  }: {
    rule: string;
    reoccurringCount: number;
    durationSecs: number;
    startsAt?: number;
  },
  timeZoneId: string,
  locale: LocaleDefinition,
) => {
  let option = locale.general.doesNotRepeat;
  const repeatOptionValue = getRepeatOptionValue(rule, reoccurringCount);
  if (reoccurringCount > 1 || reoccurringCount === REOCCUR_INDEFINITE) {
    const { minute, hour, date, month, day } = parseCron(rule);

    switch (repeatOptionValue) {
      case REPEAT_HOURLY: {
        option = getLocalizedStr({
          string: locale.general.everyHourStartingAt,
          replacements: { minutes: parseInt(minute) },
        });
        break;
      }

      case REPEAT_EVERY_15MIN: {
        option = getLocalizedStr({
          string: locale.general.every15minStartingAt,
          replacements: { time: `${padl0(hour)}:${padl0(minute)}` },
        });
        break;
      }

      case REPEAT_EVERY_30MIN: {
        option = getLocalizedStr({
          string: locale.general.every30minStartingAt,
          replacements: { time: `${padl0(hour)}:${padl0(minute)}` },
        });
        break;
      }

      case REPEAT_EVERY_4H: {
        const nextStartTime = getNextStartTime(
          { rule, reoccurringCount, durationSecs, startsAt },
          timeZoneId,
        );
        option = getLocalizedStr({
          string: 'Every 4h starting at __time__',
          replacements: { time: TimeFormatter.getTimeString({ time: nextStartTime, timeZoneId }) },
        });
        break;
      }

      case REPEAT_EVERY_6H: {
        // every 6 h
        // calculate the next start time as it depends on the current time / configured start time
        const nextStartTime = getNextStartTime(
          { rule, reoccurringCount, durationSecs, startsAt },
          timeZoneId,
        );
        option = getLocalizedStr({
          string: locale.general.every6hStartingAt,
          replacements: { time: TimeFormatter.getTimeString({ time: nextStartTime, timeZoneId }) },
        });
        break;
      }
      case REPEAT_DAILY: {
        option = getLocalizedStr({
          string: locale.general.dailyAtTime,
          replacements: { time: `${padl0(hour)}:${padl0(minute)}` },
        });
        break;
      }
      case REPEAT_WEEKLY: {
        option = getLocalizedStr({
          string: locale.general.weeklyOnDayAtTime,
          replacements: {
            day: moment().day(parseInt(day, 10)).format('dddd'),
            time: `${padl0(hour)}:${padl0(minute)}`,
          },
        });
        break;
      }
      case REPEAT_MONTHLY: {
        option = getLocalizedStr({
          string: locale.general.monthlyOnDate,
          replacements: { date },
        });
        break;
      }
      default: {
        const monthDate = moment()
          .month(parseInt(month) - 1)
          .date(parseInt(date))
          .format(monthDateFormat);
        option = getLocalizedStr({
          string: locale.general.annuallyOnDate,
          replacements: { date: monthDate },
        });
      }
    }
  }
  return option;
};

/**
 * Return the next start time if it's recurring and the closest occurrence has ENDED.
 * Otherwise, return the closest start time.
 */
export const getNextStartTime = (
  {
    rule,
    reoccurringCount,
    durationSecs,
    startsAt,
  }: {
    rule: string;
    reoccurringCount: number;
    durationSecs: number;
    startsAt?: number;
  },
  timezoneId: string,
  after?: number,
) => {
  const duration = durationSecs * 1000;
  const _target = after || startsAt || Date.now();
  const target = _target > Date.now() ? _target : Date.now();

  const startTimeOfThisDay = getStartTimeOfThisDay(target, rule, timezoneId);
  const endTimeOfThisMonth = startTimeOfThisDay + duration;
  const endTimeOfThisDay = startTimeOfThisDay + duration;

  const repeatOptionValue = getRepeatOptionValue(rule, reoccurringCount);
  switch (repeatOptionValue) {
    case REPEAT_HOURLY: {
      const { minute } = parseCron(rule);
      if (target > Date.now()) {
        /** activity is in future */
        return target;
      }
      /** an hour in advance relative to current time */
      return moment(target).minute(parseInt(minute)).add(1, 'hour').valueOf();
    }

    case REPEAT_EVERY_15MIN: {
      const { minute } = parseCron(rule);
      if (target > Date.now()) {
        /** activity is in future */
        return target;
      }
      /** 15 minutes in advance relative to current time */
      return moment(target).minute(parseInt(minute)).add(15, 'minutes').valueOf();
    }

    case REPEAT_EVERY_30MIN: {
      const { minute } = parseCron(rule);
      if (target > Date.now()) {
        /** activity is in future */
        return target;
      }
      /** 30 minutes in advance relative to current time */
      return moment(target).minute(parseInt(minute)).add(30, 'minutes').valueOf();
    }

    case REPEAT_EVERY_4H:
    case REPEAT_EVERY_6H: {
      // Combine logic for both 4h and 6h intervals
      const interval = repeatOptionValue === REPEAT_EVERY_4H ? 4 : 6;
      const hourDiff = moment(target).diff(startTimeOfThisDay, 'hour');

      if (moment(target).isAfter(startTimeOfThisDay)) {
        if (target > Date.now()) {
          return (
            moment(startTimeOfThisDay)
              // Round `hourDiff` to the next higher multiple of 4h or 6h
              .add(Math.ceil(hourDiff / interval) * interval, 'hours')
              .valueOf()
          );
        }
        return (
          moment(startTimeOfThisDay)
            // Round `hourDiff` to the next higher multiple and add the interval if needed
            .add(Math.ceil((hourDiff + 1) / interval) * interval, 'hours')
            .valueOf()
        );
      }
      return startTimeOfThisDay;
    }

    case REPEAT_DAILY: {
      if (moment(target).isAfter(endTimeOfThisDay)) {
        return moment(startTimeOfThisDay).add(1, 'day').valueOf();
      }
      return startTimeOfThisDay;
    }

    case REPEAT_WEEKLY: {
      if (moment(target).isAfter(endTimeOfThisMonth)) {
        const nextWeek = moment(target).add(1, 'week').week();
        return moment(startTimeOfThisDay).week(nextWeek).valueOf();
      }
      return startTimeOfThisDay;
    }

    case REPEAT_MONTHLY: {
      if (moment(target).isAfter(endTimeOfThisMonth)) {
        const nextMonth = moment(target).add(1, 'month').month();
        return moment(startTimeOfThisDay).month(nextMonth).valueOf();
      }
      return startTimeOfThisDay;
    }

    case REPEAT_ANNUALLY: {
      if (moment(target).isAfter(endTimeOfThisMonth)) {
        const nextYear = moment(target).add(1, 'year').year();
        return moment(startTimeOfThisDay).year(nextYear).valueOf();
      }
      return startTimeOfThisDay;
    }

    case DOES_NOT_REPEAT:
    default: {
      return startsAt || 0; // Can be in the past or future
    }
  }
};

const getStartTimeOfThisDay = (target: number, rule: string, timezoneId: string) => {
  const { minute, hour, date, month, day } = parseCron(rule);

  const monthNum = parseInt(month);
  const dateNum = parseInt(date);
  const hourNum =
    (hour &&
      hour
        .split(',')
        .map((hourComp) => parseInt(hourComp, 10))
        .sort((a, b) => a - b)[0]) ||
    0;
  const minuteNum = parseInt(minute);
  const dayNum = parseInt(day);

  const startTimeOfThisDay = moment(target)
    .tz(timezoneId)
    .date(dateNum)
    .day(dayNum)
    .hour(hourNum)
    .minute(minuteNum)
    .startOf('minute');

  if (month !== '*') {
    startTimeOfThisDay.month(monthNum - 1);
  }

  return startTimeOfThisDay.valueOf();
};

export const getDurationSecs = (startTime: Date | number, endTime: Date | number) => {
  return Math.floor((new Date(endTime).getTime() - new Date(startTime).getTime()) / 1000);
};

export const ifHasUnexecutedEvents = (
  {
    activityType,
    durationSecs,
    reason,
    rule,
    reoccurringCount,
    startsAt,
  }: {
    activityType?: string;
    reason?: string;
    durationSecs: number;
    rule: string;
    reoccurringCount: number;
    startsAt?: number;
  },
  timezoneId: string,
) => {
  if (
    reason === ScheduledActivityType.SCHEDULED_INSPECTION ||
    activityType === ScheduledActivityType.SCHEDULED_INSPECTION
  ) {
    // scheduled inspections are NOT created in advance
    return false;
  }
  const startTime = getNextStartTime(
    { rule, reoccurringCount, durationSecs, startsAt },
    timezoneId,
  );
  const diff = startTime - new Date().getTime();
  return diff < ONE_HOUR_MILLISEC && diff > 0 - durationSecs * 1000;
};

export const getUnexecutedEvents = async (
  {
    rule,
    reoccurringCount,
    durationSecs,
    deviceId,
    id,
    startsAt,
  }: {
    rule: string;
    reoccurringCount: number;
    durationSecs: number;
    deviceId?: string;
    id: string;
    startsAt?: number;
  },
  timezoneId: string,
) => {
  const startTime = getNextStartTime(
    { rule, reoccurringCount, durationSecs, startsAt },
    timezoneId,
  );
  const params = generateQueryParameters({
    start: new Date(startTime).toISOString(),
    end: new Date(startTime + durationSecs * 1000).toISOString(),
    locationId: deviceId,
    include: HIDE_FROM_DISPLAY_TAG,
    eventSubtypeFilter: { [EVENT_SUBTYPE_SCHEDULED]: true },
    eventFilterState: {
      ...DEFAULT_EVENT_FITLERS,
      bookmark: true,
      transaction: false,
      motion: false,
    },
    source: '#getUnexecutedEvents',
  });
  try {
    const { events } = await getEventsFromPlatform(params);
    return events.filter(
      (event) => event.details?.scheduledActivity?.reoccurringId === id && event.state !== 'CLOSED',
    );
  } catch (err) {
    console.error(err);
    throw err;
  }
};

export const scheduledActivityRemapper = (
  selectedCameras: { [key: string]: any },
  newActivity: NewActivityType,
  useSelectedCameras: boolean,
): NewActivityType => {
  const getReason = () => {
    // scheduled inspection have a fixed reason recognized by the parser, this is also the case for scheduled AI chat
    // TODO: remove when callhome can handle the activity type and the parser can recognize it
    if (newActivity.activityType === ScheduledActivityType.SCHEDULED_INSPECTION) {
      return ScheduledActivityType.SCHEDULED_INSPECTION;
    }
    if (newActivity.activityType === ScheduledActivityType.SCHEDULED_AI_CHAT) {
      return ScheduledActivityType.SCHEDULED_AI_CHAT;
    }
    return newActivity.reason;
  };

  const getDuration = () => {
    // duration of scheduled inspection is always 0 - only the start time is relevant for this use-case
    // this also applies to scheduled AI chat
    if (
      newActivity.activityType &&
      [
        ScheduledActivityType.SCHEDULED_INSPECTION,
        ScheduledActivityType.SCHEDULED_AI_CHAT,
      ].includes(newActivity.activityType)
    ) {
      return 0;
    }
    return newActivity.durationSecs;
  };

  const _newActivity = {
    ...newActivity,
    reason: getReason(),
    durationSecs: getDuration(),
    cameraIds:
      newActivity.activityType === ScheduledActivityType.SCHEDULED_INSPECTION || useSelectedCameras
        ? CommonUtil.mapObjectToArray(selectedCameras, 'chId')
        : [],
  };
  // callhome will not know about the activityType property and would reject the payload if it sees it
  // TODO: remove when callhome handles the activityType
  delete _newActivity.activityType;
  return _newActivity;
};

export function getScheduledExceptionsBaseAnalytics({
  startTime,
  endTime,
  recurrence,
  title,
  description,
  locationId,
  locationName,
  recurrenceOptions,
  typeOfException,
}: {
  startTime: number;
  endTime: number;
  recurrence: any;
  title: string;
  recurrenceOptions: any[];
  description: string | undefined;
  locationId: string | undefined;
  locationName: string | undefined;
  typeOfException: TypeOfAlarmException;
}): ScheduledExceptionsBaseAnalytics {
  return {
    alarmExceptionStartTime: new Date(startTime).toISOString(),
    alarmExceptionEndTime: new Date(endTime).toISOString(),
    alarmExceptionRepeat: recurrenceOptions.find((option) => option.value === recurrence)?.label,
    alarmExceptionTitle: title,
    alarmExceptionDescription: description || '',
    locationId: locationId || '',
    locationName: locationName || '',
    alarmExceptionType: typeOfException,
  };
}

export function getScheduledExceptionsPreviousEventAnalytics({
  startTime,
  endTime,
  recurrence,
  title,
  description,
  recurrenceOptions,
  typeOfException,
}: {
  startTime: number;
  endTime: number;
  recurrence: any;
  title: string | undefined;
  recurrenceOptions: any[];
  description: string | undefined;
  typeOfException: TypeOfAlarmException;
}): ScheduledExceptionsPreviousEventAnalytics {
  return {
    previousAlarmExeptionStartTime: new Date(startTime).toISOString(),
    previousAlarmExceptionEndTime: new Date(endTime).toISOString(),
    previousAlarmExceptionRepeat: recurrenceOptions.find((option) => option.value === recurrence)
      .label,
    previousAlarmExceptionTitle: title || '',
    previousAlarmExceptionDescription: description || '',
    previousAlarmExceptionType: typeOfException,
  };
}

export function getAIChatPreviewAnalytics(
  activity: NewActivityType,
  deviceType: AIInspectionDeviceType,
) {
  return {
    spotCheckTitle: activity.title,
    openAIChatPrompt: activity.metadata?.aiChatPrompt ?? '',
    deviceId: activity.deviceId,
    promptSource: deviceType,
  };
}

/**
 * @param activity
 * @param deviceType | location or camera group
 */
export function getScheduledAIChatAnalytics(
  activity: NewActivityType,
  deviceType: AIInspectionDeviceType,
  /** which part of the app generated the analytics */
  source?: string,
) {
  return {
    spotCheckStartTime: new Date(activity.startsAt).toISOString(),
    spotCheckEndTime:
      activity?.endsAt === REOCCUR_NEVER_END ? undefined : new Date(activity?.endsAt).toISOString(),
    spotCheckRepeat: getRepeatOptionValue(activity.rule, activity.reoccurringCount),
    openAIChatPrompt: activity.metadata?.aiChatPrompt ?? '',
    deviceId: activity.deviceId,
    promptSource: deviceType,
    shotCheckTitle: activity.title,
    feature: 'scheduled-spot-check',
    source,
  };
}

/** hourly repeat x 24 hours x 31 days */
const MAX_POSSIBLE_REOCCURRING_COUNT_IN_MONTH = 744;
/**
 * Calculate the number of image credits an activity will use by end of month
 */
export function getCurrentMonthImageUsage(
  activity: Pick<NewActivityType, 'startsAt' | 'endsAt' | 'reoccurringCount' | 'rule'>,
  camerasCount: number,
  timezone: string,
): number {
  if (activity.rule === '') {
    return 0;
  } else if (activity.reoccurringCount === OCCUR_ONCE) {
    return camerasCount;
  }

  let matches: string[] = [];

  /** activities with fixed reoccurring counts are configured with activity.endsAt: -1 */
  if (activity.endsAt === REOCCUR_INDEFINITE) {
    // get date (ISO strings, e.g '2024-06-10T07:00:00Z') of when all scheduled activities will execute hased on CRON string
    matches = getFutureMatches(activity.rule, {
      startAt: new Date(activity.startsAt).toISOString(),
      matchCount: activity.reoccurringCount,
      timezone,
    });
  } else {
    /** activities that end based on expiration date */
    matches = getFutureMatches(activity.rule, {
      startAt: new Date(activity.startsAt).toISOString(),
      endAt: new Date(activity.endsAt).toISOString(),
      matchCount: MAX_POSSIBLE_REOCCURRING_COUNT_IN_MONTH,
      timezone,
    });
  }

  const endOfMonthTimestamp = moment().tz(timezone).endOf('month').valueOf();

  return (
    matches.filter((match) => {
      // exclude matches prior to current time (already counted in usage quota)
      // and after end of current month (won't be counted towards current month's usage )
      return +new Date(match) > Date.now() && +new Date(match) < endOfMonthTimestamp;
    }).length * camerasCount
  );
}

export function isScheduledActivityOngoing({
  rule,
  reoccurringCount,
  startsAt,
  endsAt,
  durationSecs,
}: {
  rule: string;
  reoccurringCount: number;
  startsAt: number;
  endsAt: number;
  durationSecs: number;
}) {
  const currentTime = Date.now();

  if ((endsAt < currentTime && endsAt !== REOCCUR_NEVER_END) || reoccurringCount === OCCUR_NEVER) {
    return false;
  }

  if (endsAt === REOCCUR_NEVER_END && reoccurringCount === REOCCUR_INDEFINITE) {
    return true;
  }

  const repeatOptionValue = getRepeatOptionValue(rule, reoccurringCount);

  switch (repeatOptionValue) {
    case REPEAT_EVERY_15MIN: {
      return isWithinActiveTimePeriod({
        startsAt,
        endsAt,
        currentTime,
        numberOfDateUnitToAdd: Math.ceil(((reoccurringCount - 1) * 15) / 60), // Convert minutes to hours
        durationSecs,
        timeSpan: 'hours',
        interval: 0.25, // 15 minutes = 0.25 hours
      });
    }

    case REPEAT_EVERY_30MIN: {
      return isWithinActiveTimePeriod({
        startsAt,
        endsAt,
        currentTime,
        numberOfDateUnitToAdd: Math.ceil(((reoccurringCount - 1) * 30) / 60), // Convert minutes to hours
        durationSecs,
        timeSpan: 'hours',
        interval: 0.5, // 30 minutes = 0.5 hours
      });
    }

    case REPEAT_HOURLY: {
      return isWithinActiveTimePeriod({
        startsAt,
        endsAt,
        currentTime,
        numberOfDateUnitToAdd: reoccurringCount - 1,
        durationSecs,
        timeSpan: 'hours',
      });
    }

    case REPEAT_EVERY_6H: {
      const { day } = parseCron(rule);
      return isWithinActiveTimePeriod({
        startsAt,
        endsAt,
        currentTime,
        numberOfDateUnitToAdd: isAllDaysOfWeek(day)
          ? reoccurringCount - 1
          : getTotalTimeUnitsToNextOccurrence({
              currentTime,
              startsAt,
              day,
              reoccurringCount,
              timeSpan: 'hours',
              interval: 6,
            }),
        durationSecs,
        timeSpan: 'hours',
        interval: 6,
      });
    }

    case REPEAT_EVERY_4H: {
      const { day } = parseCron(rule);
      return isWithinActiveTimePeriod({
        startsAt,
        endsAt,
        currentTime,
        numberOfDateUnitToAdd: isAllDaysOfWeek(day)
          ? reoccurringCount - 1
          : getTotalTimeUnitsToNextOccurrence({
              currentTime,
              startsAt,
              day,
              reoccurringCount,
              timeSpan: 'hours',
              interval: 4,
            }),
        durationSecs,
        timeSpan: 'hours',
        interval: 4,
      });
    }

    case REPEAT_DAILY: {
      const { day } = parseCron(rule);
      return isWithinActiveTimePeriod({
        startsAt,
        endsAt,
        currentTime,
        numberOfDateUnitToAdd: isAllDaysOfWeek(day)
          ? reoccurringCount - 1
          : getTotalTimeUnitsToNextOccurrence({
              currentTime,
              startsAt,
              day,
              reoccurringCount,
              timeSpan: 'days',
            }),
        durationSecs,
        timeSpan: 'days',
      });
    }

    case REPEAT_WEEKLY: {
      return isWithinActiveTimePeriod({
        startsAt,
        endsAt,
        currentTime,
        numberOfDateUnitToAdd: reoccurringCount - 1,
        durationSecs,
        timeSpan: 'weeks',
      });
    }

    case REPEAT_MONTHLY: {
      return isWithinActiveTimePeriod({
        startsAt,
        endsAt,
        currentTime,
        numberOfDateUnitToAdd: reoccurringCount - 1,
        durationSecs,
        timeSpan: 'months',
      });
    }

    case REPEAT_ANNUALLY: {
      return isWithinActiveTimePeriod({
        startsAt,
        endsAt,
        currentTime,
        numberOfDateUnitToAdd: reoccurringCount - 1,
        durationSecs,
        timeSpan: 'years',
      });
    }

    case DOES_NOT_REPEAT: {
      return currentTime <= startsAt;
    }
    default: {
      return false;
    }
  }
}

function isWithinActiveTimePeriod({
  startsAt,
  endsAt,
  currentTime,
  numberOfDateUnitToAdd,
  durationSecs,
  timeSpan,
  interval = 1,
}: {
  startsAt: number;
  endsAt: number;
  currentTime: number;
  numberOfDateUnitToAdd: number;
  durationSecs: number;
  interval?: number;
  timeSpan: 'hours' | 'days' | 'weeks' | 'months' | 'years';
}) {
  const lastOccurrence =
    moment(startsAt)
      .add(numberOfDateUnitToAdd * interval, timeSpan)
      .valueOf() + durationSecs;

  const endTime = endsAt > 0 ? endsAt : lastOccurrence;
  return currentTime <= endTime;
}

function getTotalTimeUnitsToNextOccurrence({
  startsAt,
  currentTime,
  day,
  reoccurringCount,
  interval = 1, // Default interval for time units (e.g., 1 day, 6 hours)
  timeSpan = 'days', // 'days' or 'hours' or other units
}: {
  startsAt: number;
  currentTime: number;
  day: string;
  reoccurringCount: number;
  interval?: number;
  timeSpan: 'hours' | 'days';
}) {
  let currentDayOfWeek = moment(startsAt).day(); // Get the current day of the week
  const daysOfWeek = day.split(',').map(Number); // Convert the string of days into an array of numbers
  const sortedDaysOfWeek = daysOfWeek.sort((a, b) => a - b); // Sort days in ascending order

  let totalUnits = 0; // This will represent the total units (hours or days)
  let occurrences = 0;
  let currentStartDate = moment(startsAt);
  let isFirstOccurrence = true;

  while (occurrences < reoccurringCount) {
    // Find the next valid day of the week
    const nextDay =
      isFirstOccurrence && startsAt >= currentTime
        ? (sortedDaysOfWeek.find((d) => d >= currentDayOfWeek) ?? sortedDaysOfWeek[0])
        : (sortedDaysOfWeek.find((d) => d > currentDayOfWeek) ?? sortedDaysOfWeek[0]);

    let daysToAdd = (nextDay - currentDayOfWeek + 7) % 7;
    // daysToAdd === 0 only valid on first occurrence when start is in the future (today counts).
    // Otherwise same-day match means wrap to next week.
    if (daysToAdd === 0 && !(isFirstOccurrence && startsAt >= currentTime)) {
      daysToAdd = 7;
    }

    isFirstOccurrence = false;
    currentStartDate = currentStartDate.add(daysToAdd, 'days');

    if (timeSpan === 'hours') {
      // Calculate the total hours to add within this day, based on the interval
      const hoursPassedInCurrentDay = currentStartDate.diff(startsAt, 'hours') % interval;
      const hoursToAddInCurrentDay =
        hoursPassedInCurrentDay === 0 ? 0 : interval - hoursPassedInCurrentDay;

      // Add the total hours (days converted to hours + hours within the day)
      totalUnits += daysToAdd * 24 + hoursToAddInCurrentDay;
    } else if (timeSpan === 'days') {
      // Add the total days to reach the next occurrence
      totalUnits += daysToAdd;
    }

    occurrences += 1; // Increment the occurrence count
    currentDayOfWeek = currentStartDate.day(); // Update the current day of the week
  }

  return totalUnits; // Return total hours or days depending on the timeSpan
}
